import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { membershipPayloadFields } from '@/lib/membershipClass';
import ReactDOM from 'react-dom';
import { Calendar } from '@/components/ui/calendar';
import TimeRangePicker from '@/components/TimeRangePicker';
import GutscheinInput from '@/components/GutscheinInput';
import PaymentMethodModal from '@/components/PaymentMethodModal';
import CourtOverview from '@/components/CourtOverview';
import { fetchEventsForDay, isSlotBooked, getCalendarIdForCourt } from '@/lib/gcal';
import { normalizeCourtKey, type CourtKey } from '@/lib/courts';
import { buildFromToISO } from '@/lib/timeUtils';
import { verifyBookingMembership } from '@/lib/membershipVerification';
import { applyMultipleGutscheine, validateMultipleGutscheine, calculateGutscheinDiscount, type AppliedGutschein } from '@/lib/gutschein';
import { buildBookingPayload } from '@/lib/buildBookingPayload';
import { normalizeBookingResult, parseErrorResponse } from '@/lib/normalizeBookingResult';

interface BookingPageProps {
  onNavigate?: (page: 'home' | 'booking' | 'membership' | 'contact' | 'impressum' | 'datenschutz' | 'buchungsbedingungen' | 'cookie-einstellungen' | 'paypal-return' | 'paypal-cancel' | 'success' | 'error' | 'cancel-free') => void;
}

const BookingPage: React.FC<BookingPageProps> = ({ onNavigate }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedEndTime, setSelectedEndTime] = useState('');
  const [selectedCourt, setSelectedCourt] = useState('');
  const [courtType, setCourtType] = useState<'tennis' | 'padel'>('tennis');
  const [membershipType, setMembershipType] = useState<'adult-guest' | 'passive' | 'youth' | 'padel-member' | 'student' | 'guest'>('adult-guest');
  const [padelPlayerCount, setPadelPlayerCount] = useState<number>(2);
  const [tennisPlayerCount, setTennisPlayerCount] = useState<number>(2);
  const [playerMemberships, setPlayerMemberships] = useState<Array<'guest' | 'student' | 'member'>>(['guest', 'guest', 'guest', 'guest']);
  const [playerMembershipNumbers, setPlayerMembershipNumbers] = useState<string[]>(['', '', '', '']);
  const [playerLastNames, setPlayerLastNames] = useState<string[]>(['', '', '', '']);
  const [playerMembershipData, setPlayerMembershipData] = useState<Array<{first_name: string; last_name: string; membership_type: string} | null>>([null, null, null, null]);
  const [playerMembershipErrors, setPlayerMembershipErrors] = useState<string[]>(['', '', '', '']);
  const [expandedPlayers, setExpandedPlayers] = useState<boolean[]>([false, false, false, false]);
  const [entryMode, setEntryMode] = useState<'guest' | 'member' | 'none'>('none');
  const [membershipNumber] = useState('');
  const [showMemberConfirmation] = useState(false);
  const [isConfirmationFadingOut] = useState(false);
  const [personalFullName, setPersonalFullName] = useState('');
  const [floodlight, setFloodlight] = useState(false);
  const [racketRental, setRacketRental] = useState(false);
  const [racketRentalQuantity, setRacketRentalQuantity] = useState(1);
  const [ballRental, setBallRental] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [bookedSlots, setBookedSlots] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [appliedGutscheine, setAppliedGutscheine] = useState<AppliedGutschein[]>([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [isFreeBookingProcessing, setIsFreeBookingProcessing] = useState(false);
  const [freeBookingError, setFreeBookingError] = useState('');
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [mobileBarExpanded, setMobileBarExpanded] = useState(false);
  const mobileBarRef = useRef<HTMLDivElement>(null);

  // Validation state
  const [validationErrors, setValidationErrors] = useState({
    courtType: false,
    court: false,
    membership: false,
    entryMode: false,
    dateTime: false,
    personalData: false,
    terms: false,
  });

  // Refs for scrolling to sections
  const courtTypeRef = useRef<HTMLDivElement>(null);
  const dateTimeRef = useRef<HTMLDivElement>(null);
  const membershipRef = useRef<HTMLDivElement>(null);
  const personalDataRef = useRef<HTMLDivElement>(null);
  const termsRef = useRef<HTMLDivElement>(null);
  const confirmationPopupRef = useRef<HTMLDivElement>(null);
  const bookingFormRef = useRef<HTMLDivElement>(null);

  // Memoize timeSlots to prevent recreation on every render
  const timeSlots = useMemo(
    () =>
      Array.from({ length: 28 }, (_, i) => {
        const totalMinutes = i * 30;
        const hour = Math.floor(totalMinutes / 60) + 8;
        const minute = totalMinutes % 60;
        return `${hour.toString().padStart(2, '0')}:${minute
          .toString()
          .padStart(2, '0')}`;
      }),
    []
  );

  const fetchBookedSlots = useCallback(async () => {
    if (!date || !selectedCourt) return;

    // Use local date components (Europe/Berlin) - no UTC conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;

    // Normalize court label to canonical key (e.g., "Platz 1" -> "tennis-1" or "padel-1")
    const courtLabel = `${courtType === 'tennis' ? 'Tennis' : 'Padel'} ${
      selectedCourt.match(/\d+/)?.[0] || '1'
    }`;
    const courtKey = normalizeCourtKey(courtLabel);

    if (!courtKey) {
      console.warn('[BookingPage] Could not normalize court:', courtLabel);
      setBookedSlots(new Set());
      return;
    }

    // Get calendar ID for this court
    const calendarId = getCalendarIdForCourt(courtKey);
    if (!calendarId) {
      console.warn('[BookingPage] No calendar ID for court:', courtKey);
      setBookedSlots(new Set());
      return;
    }

    try {
      // Fetch events from Google Calendar
      const events = await fetchEventsForDay(calendarId, dateString);
      console.log(`[BookingPage] Fetched ${events.length} events for ${courtKey} on ${dateString}:`, events);

      // Mark slots as booked based on calendar events
      const booked = new Set<string>();
      timeSlots.forEach((slot) => {
        const startLocal = `${dateString}T${slot}`;
        try {
          const { from, to } = buildFromToISO(startLocal, 30);
          const isBooked = isSlotBooked(from, to, events);
          console.log(`[BookingPage] Checking slot ${slot}: from=${from}, to=${to}, isBooked=${isBooked}`);
          if (isBooked) {
            booked.add(slot);
          }
        } catch (err) {
          console.warn(`[BookingPage] Error parsing time slot ${slot}:`, err);
        }
      });

      console.log(`[BookingPage] Booked slots for ${courtKey}:`, Array.from(booked));
      setBookedSlots(booked);
    } catch (err) {
      console.error('[BookingPage] Error fetching calendar events:', err);
      setBookedSlots(new Set());
    }
  }, [date, selectedCourt, courtType, timeSlots]);

  useEffect(() => {
    if (date && selectedCourt) {
      fetchBookedSlots();
    }
  }, [date, selectedCourt, courtType, fetchBookedSlots]);

  useEffect(() => {
    if (showMemberConfirmation && !isConfirmationFadingOut && confirmationPopupRef.current) {
      setTimeout(() => {
        confirmationPopupRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }, 100);
    }
  }, [showMemberConfirmation, isConfirmationFadingOut]);

  useEffect(() => {
    if (!mobileBarExpanded) return;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (mobileBarRef.current && !mobileBarRef.current.contains(e.target as Node)) {
        setMobileBarExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [mobileBarExpanded]);

  // Convert booked slots to busy intervals for TimeRangePicker
  const busyIntervals = useMemo(() => {
    if (!date || !selectedCourt) return [];

    const intervals: Array<{ start: string; end: string; reason?: 'past' | 'booked' }> = [];
    const sortedSlots = Array.from(bookedSlots).sort();

    // Check if selected date is today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    const isToday = selectedDate.getTime() === today.getTime();

    // If today, add past time slots as busy intervals
    if (isToday) {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentTotalMinutes = currentHour * 60 + currentMinute;

      // Block all time slots from 08:00 to current time
      const startOfDay = 8 * 60; // 08:00 in minutes

      if (currentTotalMinutes > startOfDay) {
        // Round down to current 30-minute slot (keep current slot bookable)
        const blockedUntilMinutes =
          Math.floor(currentTotalMinutes / 30) * 30;
        const blockedHour = Math.floor(blockedUntilMinutes / 60);
        const blockedMin = blockedUntilMinutes % 60;
        const blockedUntil = `${blockedHour
          .toString()
          .padStart(2, '0')}:${blockedMin
          .toString()
          .padStart(2, '0')}`;

        intervals.push({ start: '08:00', end: blockedUntil, reason: 'past' });
      }
    }

    let rangeStart: string | null = null;
    let rangeEnd: string | null = null;

    sortedSlots.forEach((slot, idx) => {
      if (rangeStart === null) {
        rangeStart = slot;
        rangeEnd = slot;
      } else {
        const [prevH, prevM] = rangeEnd!.split(':').map(Number);
        const [currH, currM] = slot.split(':').map(Number);

        const prevMinutes = prevH * 60 + prevM;
        const currMinutes = currH * 60 + currM;

        if (currMinutes === prevMinutes + 30) {
          rangeEnd = slot;
        } else {
          const [endH, endM] = rangeEnd!.split(':').map(Number);
          const endMinutes = endH * 60 + endM + 30;
          const endHour = Math.floor(endMinutes / 60)
            .toString()
            .padStart(2, '0');
          const endMin = (endMinutes % 60).toString().padStart(2, '0');

          intervals.push({
            start: rangeStart,
            end: `${endHour}:${endMin}`,
            reason: 'booked'
          });

          rangeStart = slot;
          rangeEnd = slot;
        }
      }

      if (idx === sortedSlots.length - 1 && rangeStart !== null) {
        const [endH, endM] = rangeEnd!.split(':').map(Number);
        const endMinutes = endH * 60 + endM + 30;
        const endHour = Math.floor(endMinutes / 60)
          .toString()
          .padStart(2, '0');
        const endMin = (endMinutes % 60).toString().padStart(2, '0');

        intervals.push({
          start: rangeStart,
          end: `${endHour}:${endMin}`,
          reason: 'booked'
        });
      }
    });

    return intervals;
  }, [bookedSlots, date, selectedCourt]);

  const handleDateSelect = (newDate: Date | undefined) => {
    if (!newDate) return;
    setDate(newDate);
    setSelectedTime('');
    setSelectedEndTime('');
  };

  const getDisabledDays = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 7);

    return [{ before: today }, { after: maxDate }];
  };

  const formatDateDisplay = (date: Date): string => {
    const days = [
      'Sonntag',
      'Montag',
      'Dienstag',
      'Mittwoch',
      'Donnerstag',
      'Freitag',
      'Samstag',
    ];
    const months = [
      'Januar',
      'Februar',
      'März',
      'April',
      'Mai',
      'Juni',
      'Juli',
      'August',
      'September',
      'Oktober',
      'November',
      'Dezember',
    ];

    return `${days[date.getDay()]}, ${date.getDate()}. ${
      months[date.getMonth()]
    } ${date.getFullYear()}`;
  };

  // Get canonical court key for the selected court
  const getCourtKey = () => {
    if (!selectedCourt) return '';
    const courtNumber = selectedCourt.match(/\d+/)?.[0] || '1';
    const courtLabel = `${courtType === 'tennis' ? 'Tennis' : 'Padel'} ${courtNumber}`;
    return normalizeCourtKey(courtLabel) || '';
  };

  const getDaysUntilBooking = (): string => {
    if (!date) return '';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const booking = new Date(date);
    booking.setHours(0, 0, 0, 0);

    const diff = Math.ceil(
      (booking.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diff === 0) return 'Heute';
    if (diff === 1) return 'Morgen';

    return `in ${diff} Tagen`;
  };


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateAndScrollToIncomplete = () => {
    const errors = {
      courtType: false,
      court: false,
      membership: false,
      entryMode: false,
      dateTime: false,
      personalData: false,
      terms: false,
    };

    let isValid = true;
    let firstErrorRef: React.RefObject<HTMLDivElement> | null = null;

    // Check if court is selected
    if (!selectedCourt) {
      errors.court = true;
      isValid = false;
      if (!firstErrorRef) firstErrorRef = courtTypeRef;
    }

    // Check court type (always selected by default, but check player count)
    if (courtType === 'tennis' && tennisPlayerCount === 0) {
      errors.courtType = true;
      isValid = false;
      if (!firstErrorRef) firstErrorRef = courtTypeRef;
    }

    if (courtType === 'padel' && padelPlayerCount === 0) {
      errors.courtType = true;
      isValid = false;
      if (!firstErrorRef) firstErrorRef = courtTypeRef;
    }

    // Check entry mode
    if (entryMode === 'none') {
      errors.entryMode = true;
      isValid = false;
      if (!firstErrorRef) firstErrorRef = membershipRef;
    }

    // Check date and time
    if (!selectedDate || !selectedTime || !selectedEndTime) {
      errors.dateTime = true;
      isValid = false;
      if (!firstErrorRef) firstErrorRef = dateTimeRef;
    }

    // Check personal data
    if (!personalFullName || !formData.email || !formData.phone) {
      errors.personalData = true;
      isValid = false;
      if (!firstErrorRef) firstErrorRef = personalDataRef;
    }

    // Check terms
    if (!acceptTerms) {
      errors.terms = true;
      isValid = false;
      if (!firstErrorRef) firstErrorRef = termsRef;
    }

    setValidationErrors(errors);

    if (!isValid && firstErrorRef) {
      firstErrorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }

    return isValid;
  };

  const handleBooking = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateAndScrollToIncomplete()) {
      setShowPaymentModal(true);
    }
  };

  const getCourtPrice = () => {
    const currentPlayerCount = courtType === 'tennis' ? tennisPlayerCount : padelPlayerCount;

    // Check if booking starts before or after 4pm (16:00)
    const isAfter4pm = selectedTime && parseInt(selectedTime.split(':')[0]) >= 16;

    let totalHourlyRate = 0;
    for (let i = 0; i < currentPlayerCount; i++) {
      const membership = playerMemberships[i] || 'guest';
      const memberData = playerMembershipData[i];

      if (membership === 'guest') {
        totalHourlyRate += isAfter4pm ? 8 : 6;
      } else if (membership === 'student') {
        totalHourlyRate += isAfter4pm ? 6 : 5;
      } else if (membership === 'member' && memberData) {
        // Check if member has matching membership type for the court
        if (courtType === 'tennis' && memberData.membership_type === 'tennis') {
          totalHourlyRate += 0;
        } else if (courtType === 'padel' && memberData.membership_type === 'padel') {
          totalHourlyRate += 0;
        } else if (memberData.membership_type === 'combination') {
          // Combination membership = free on both padel and tennis courts
          totalHourlyRate += 0;
        } else {
          // Member but not for this court type
          totalHourlyRate += isAfter4pm ? 5 : 4;
        }
      } else {
        // Member but not verified yet, count as guest
        totalHourlyRate += isAfter4pm ? 8 : 6;
      }
    }

    return totalHourlyRate;
  };

  const calculateDurationHours = useCallback((): number => {
    if (!selectedTime || !selectedEndTime) return 1;

    const [startHour, startMin] = selectedTime.split(':').map(Number);
    const [endHour, endMin] = selectedEndTime.split(':').map(Number);

    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    return (endMinutes - startMinutes) / 60;
  }, [selectedTime, selectedEndTime]);

  // Returns an array of time segments with per-player rates for each segment.
  // Splits bookings at the 16:00 price boundary.
  const getPriceBreakdown = useCallback(() => {
    if (!selectedTime || !selectedEndTime) return [];

    const [startH, startM] = selectedTime.split(':').map(Number);
    const [endH, endM] = selectedEndTime.split(':').map(Number);
    const startMin = startH * 60 + startM;
    const endMin = endH * 60 + endM;
    const boundaryMin = 16 * 60;

    const currentPlayerCount = courtType === 'tennis' ? tennisPlayerCount : padelPlayerCount;

    const rateForPlayer = (idx: number, after4: boolean): number => {
      const membership = playerMemberships[idx] || 'guest';
      const memberData = playerMembershipData[idx];
      if (membership === 'guest') return after4 ? 8 : 6;
      if (membership === 'student') return after4 ? 6 : 5;
      if (membership === 'member' && memberData) {
        if (courtType === 'tennis' && memberData.membership_type === 'tennis') return 0;
        if (courtType === 'padel' && memberData.membership_type === 'padel') return 0;
        if (memberData.membership_type === 'combination') return 0;
        return after4 ? 5 : 4;
      }
      return after4 ? 8 : 6;
    };

    const segments: Array<{
      from: string; to: string; minutes: number; after4: boolean;
      players: Array<{ idx: number; ratePerHour: number; cost: number }>;
      segmentCost: number;
    }> = [];

    const addSegment = (fromMin: number, toMin: number, after4: boolean) => {
      if (toMin <= fromMin) return;
      const minutes = toMin - fromMin;
      const hours = minutes / 60;
      const fmt = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
      const players = Array.from({ length: currentPlayerCount }, (_, i) => {
        const rate = rateForPlayer(i, after4);
        return { idx: i, ratePerHour: rate, cost: rate * hours };
      });
      const segmentCost = players.reduce((s, p) => s + p.cost, 0);
      segments.push({ from: fmt(fromMin), to: fmt(toMin), minutes, after4, players, segmentCost });
    };

    if (startMin >= boundaryMin) {
      addSegment(startMin, endMin, true);
    } else if (endMin <= boundaryMin) {
      addSegment(startMin, endMin, false);
    } else {
      addSegment(startMin, boundaryMin, false);
      addSegment(boundaryMin, endMin, true);
    }

    return segments;
  }, [selectedTime, selectedEndTime, courtType, tennisPlayerCount, padelPlayerCount, playerMemberships, playerMembershipData]);

  const getRacketRentalPrice = () => {
    if (courtType === 'padel' && racketRental) {
      return 3 * racketRentalQuantity;
    }
    return 0;
  };

  const calculateSubtotal = () => {
    const hours = calculateDurationHours();
    const breakdown = getPriceBreakdown();
    const courtCost = breakdown.length > 0
      ? breakdown.reduce((s, seg) => s + seg.segmentCost, 0)
      : getCourtPrice() * hours;

    let total = courtCost;
    if (floodlight) total += 3 * hours;
    total += getRacketRentalPrice();
    if (ballRental) total += 3;

    return total;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const { finalAmount } = calculateGutscheinDiscount(appliedGutscheine, subtotal);
    return finalAmount;
  };

  const getGutscheinDiscount = () => {
    const subtotal = calculateSubtotal();
    const { totalDiscount } = calculateGutscheinDiscount(appliedGutscheine, subtotal);
    return totalDiscount;
  };

  const buildPlayersInfo = () => {
    const count = courtType === 'tennis' ? tennisPlayerCount : padelPlayerCount;
    return Array.from({ length: count }, (_, i) => {
      const membership = playerMemberships[i] || 'guest';
      const data = playerMembershipData[i];
      return {
        player: i + 1,
        membership_category: membership,
        ...(data ? {
          name: `${data.first_name} ${data.last_name}`.trim(),
          membership_type: data.membership_type,
        } : {}),
      };
    });
  };

  const handleFreeBooking = async () => {
    if (!validateAndScrollToIncomplete()) {
      return;
    }

    setFreeBookingError('');
    setIsFreeBookingProcessing(true);

    try {
      // Validate all gift codes before touching anything
      if (appliedGutscheine.length > 0) {
        const validation = await validateMultipleGutscheine(appliedGutscheine);
        if (!validation.success) {
          setFreeBookingError(validation.error || "Ungültiger Gutscheincode. Bitte überprüfen Sie Ihre Gutscheine.");
          setIsFreeBookingProcessing(false);
          return;
        }
      }

      const payload = buildBookingPayload();

      let response: Response;
      try {
        response = await fetch('https://n8n.cogniiq.co/webhook/free', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...payload,
            phone: formData.phone,
            // The same canonical membership keys the Stripe and PayPal paths
            // send, so all three booking routes store the identical fields.
            ...membershipPayloadFields(buildPlayersInfo()),
          }),
        });
      } catch (networkErr) {
        console.error("Free booking network error:", networkErr);
        setFreeBookingError("Verbindungsfehler. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.");
        setIsFreeBookingProcessing(false);
        return;
      }

      let data: Partial<{
        success: boolean;
        bookingConfirmed: boolean;
        paymentRefunded: boolean;
        title: string;
        message: string;
        refundMessage: string;
        nextStep: string;
        contact: string;
        redirectUrl: string;
        /*
          Sent by the workflow on a slot conflict and read twice below, but
          missing from this declaration — so `data?.reason` was a type error
          and the conflict branch could never be narrowed correctly. The
          runtime guards were right; the shape was incomplete.
        */
        reason: string;
      }> | null = null;
      try {
        const ct = response.headers.get('content-type') ?? '';
        if (ct.includes('application/json')) {
          data = await response.json();
        } else {
          data = await parseErrorResponse(response);
        }
      } catch {
        data = null;
      }
      console.info("[FreeBooking] status:", response.status, "body:", data);

      // Conflict: 409 or explicit failure — let n8n control the redirect via
      // its "Respond to Webhook" node, falling back to a local conflict page.
      const isConflict =
        response.status === 409 ||
        data?.success === false ||
        data?.bookingConfirmed === false ||
        data?.reason === 'slot_unavailable';

      if (isConflict) {
        const rawRedirect = typeof data?.redirectUrl === 'string' ? data.redirectUrl : '';
        const isSafeRelative =
          rawRedirect.startsWith('/') &&
          !rawRedirect.startsWith('//') &&
          !rawRedirect.includes('\\') &&
          !/[^\w\-./?=&%]/.test(rawRedirect.slice(1));
        const targetPath = isSafeRelative ? rawRedirect : '/booking/slot-unavailable';

        const conflictState = {
          reason: data?.reason ?? 'slot_unavailable',
          message:
            data?.message ??
            'Diese Zeit ist leider gerade nicht mehr verfügbar. Bitte wähle eine andere Zeit.',
        };

        window.history.pushState(conflictState, '', targetPath);
        window.dispatchEvent(new PopStateEvent('popstate', { state: conflictState }));
        setIsFreeBookingProcessing(false);
        return;
      }

      // Other non-2xx without a conflict body
      if (!response.ok) {
        console.error("Free booking non-2xx:", response.status);
        setFreeBookingError("Die Buchung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.");
        setIsFreeBookingProcessing(false);
        return;
      }

      // Confirmed success — redeem gutscheine and register floodlight
      const bookingConfirmed = data?.success === true && data?.bookingConfirmed !== false;

      if (bookingConfirmed && appliedGutscheine.length > 0) {
        const subtotal = calculateSubtotal();
        const result = await applyMultipleGutscheine(
          appliedGutscheine,
          subtotal,
          formData.email
        );
        if (!result.success) {
          console.error("Gutschein redemption error after booking:", result.error);
        }
      }

      if (bookingConfirmed) {
        const bookingId = `${payload.courtKey}-${payload.start}`;
        import("@/lib/floodlightClient").then((mod) => {
          mod.registerFloodlight(
            bookingId,
            payload.courtKey,
            payload.start,
            buildFromToISO(payload.start, parseInt(payload.duration, 10)).to,
            floodlight
          ).catch((err) => {
            console.error("Floodlight registration failed (non-blocking):", err);
          });
        }).catch((err) => {
          console.error("Floodlight module load failed:", err);
        });
      }

      // If n8n's "Respond to Webhook" node provides a redirectUrl, always honour it.
      // This lets n8n fully control where the user lands after a free booking.
      if (data?.redirectUrl) {
        window.location.assign(data.redirectUrl);
        return;
      }

      // Fallback: build /booking/success URL locally when n8n does not supply a redirect.
      const normalized = normalizeBookingResult(data ?? { success: true, bookingConfirmed: true }, {
        paymentCharged: false,
      });
      const resultUrl = new URL('/booking/success', window.location.origin);
      resultUrl.searchParams.set('result', JSON.stringify(normalized));
      resultUrl.searchParams.set('court', payload.courtKey);
      resultUrl.searchParams.set('start', payload.start);
      resultUrl.searchParams.set('name', personalFullName);
      window.location.assign(resultUrl.toString());
    } catch (err) {
      console.error("Free booking unexpected error:", err);
      setFreeBookingError("Die Buchung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.");
      setIsFreeBookingProcessing(false);
    }
  };

  const handleApplyGutschein = async (code: string, value: number) => {
    // Only add to local state — actual DB deduction happens at booking confirmation
    setAppliedGutscheine([
      ...appliedGutscheine,
      { code, value, originalValue: value },
    ]);
  };

  const handleRemoveGutschein = (code: string) => {
    setAppliedGutscheine(appliedGutscheine.filter(g => g.code !== code));
  };

  // Prefill booking form from CourtOverview slot selection
  const handleOverviewSlotSelect = useCallback((courtKey: CourtKey, time: string, selectedDay: Date) => {
    // Set date
    setDate(selectedDay);
    setSelectedTime('');
    setSelectedEndTime('');

    // Determine sport and court number from key
    const isPadel = courtKey.startsWith('padel');
    const courtNum = courtKey.split('-')[1];
    const courtLabel = `Platz ${courtNum}`;

    // Set court type
    if (isPadel) {
      setCourtType('padel');
      setMembershipType('guest');
      setPlayerMemberships(['guest', 'guest', 'guest', 'guest']);
    } else {
      setCourtType('tennis');
      setMembershipType('adult-guest');
      setPlayerMemberships(['guest', 'guest', 'guest', 'guest']);
    }

    // Set court (after type so the correct courts appear)
    setSelectedCourt(courtLabel);

    // Set start time (after a tick so downstream effects can settle)
    setTimeout(() => {
      setSelectedTime(time);
    }, 50);
  }, []);

  return (
    <div className="min-h-screen svh-canvas">
      <div className="relative svh-band--ink overflow-hidden">
        <img src="/photos/padel-door.jpg" alt="Booking" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#14171B]/70 via-[#14171B]/80 to-[#14171B]/95" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 lg:px-10 pt-28 pb-16 sm:pb-20">
          <div className="svh-opener__tag mb-6">
            <span className="svh-label">SV Heinersreuth</span>
          </div>
          <h1 className="svh-display-xl mb-6">
            Platz buchen
          </h1>
          <div className="svh-split lg:items-end">
            <p className="svh-lead max-w-md">
              Wählen Sie Ihren Platz, Datum und Uhrzeit — sofortige Bestätigung per E-Mail.
            </p>
            <div className="flex flex-wrap gap-x-10 gap-y-4 lg:justify-end">
              <div>
                <p className="svh-label mb-1.5">Spielzeiten</p>
                <p className="text-[16px] font-semibold text-white">08:00 – 23:00</p>
              </div>
              <div>
                <p className="svh-label mb-1.5">Flutlicht</p>
                <p className="text-[16px] font-semibold text-white">bis 23:00 Uhr</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">

        {/* ── Court Overview ── */}
        <CourtOverview
          onSelectSlot={handleOverviewSlotSelect}
          bookingFormRef={bookingFormRef}
        />

        {/* ── Booking Form anchor ── */}
        <div ref={bookingFormRef} className="scroll-mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            <form
              onSubmit={handleBooking}
              className="flex flex-col gap-5 sm:gap-6"
            >
              {/* Court Type Selection */}
              <div
                ref={courtTypeRef}
                className={`svh-card svh-card-lg p-6 sm:p-8 transition-all ${
                  validationErrors.courtType || validationErrors.court
                    ? 'bg-red-50 border-2 border-red-300'
                    : ''
                }`}
              >
                <div className="svh-stage">
                  <span className="svh-stage__n">01</span>
                  <h2 className="svh-stage__t">Platz</h2>
                  <span className="svh-stage__req">erforderlich</span>
                </div>
                <h3 className="svh-label mb-4">
                  Platztyp wählen
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setCourtType('tennis');
                      setMembershipType('adult-guest');
                      setSelectedCourt('');
                      // Reset memberships to guest for tennis
                      setPlayerMemberships(['guest', 'guest', 'guest', 'guest']);
                    }}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      courtType === 'tennis'
                        ? 'border-red-600 bg-red-50 text-red-900'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-left">
                      <h4 className="font-semibold">Tennis</h4>
                      <p className="text-sm text-gray-600">
                        3 Plätze verfügbar
                      </p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setCourtType('padel');
                      setMembershipType('guest');
                      setSelectedCourt('');
                      // Reset memberships to guest for padel
                      setPlayerMemberships(['guest', 'guest', 'guest', 'guest']);
                    }}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      courtType === 'padel'
                        ? 'border-red-600 bg-red-50 text-red-900'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-left">
                      <h4 className="font-semibold">Padel-Tennis</h4>
                      <p className="text-sm text-gray-600">
                        2 Plätze verfügbar
                      </p>
                    </div>
                  </button>
                </div>

                {/* Court Selection for Tennis */}
                {courtType === 'tennis' && (
                  <div className="mt-6 animate-fade-in">
                    <h4 className="text-md font-medium mb-3">
                      Platz auswählen{' '}
                      <span className="text-red-600 text-sm font-normal">
                        (erforderlich)
                      </span>
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {['Platz 1', 'Platz 2', 'Platz 3'].map((court) => (
                        <button
                          key={court}
                          type="button"
                          onClick={() => setSelectedCourt(court)}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            selectedCourt === court
                              ? 'border-red-600 bg-red-600 text-white'
                              : 'border-gray-200 hover:border-red-300 text-gray-700'
                          }`}
                        >
                          <div className="font-medium">{court}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Court Selection for Padel Tennis */}
                {courtType === 'padel' && (
                  <div className="mt-6 animate-fade-in">
                    <h4 className="text-md font-medium mb-3">
                      Platz auswählen{' '}
                      <span className="text-red-600 text-sm font-normal">
                        (erforderlich)
                      </span>
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {['Platz 1', 'Platz 2'].map((court) => (
                        <button
                          key={court}
                          type="button"
                          onClick={() => setSelectedCourt(court)}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            selectedCourt === court
                              ? 'border-red-600 bg-red-600 text-white'
                              : 'border-gray-200 hover:border-red-300 text-gray-700'
                          }`}
                        >
                          <div className="font-medium">{court}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Player Count Selection for Tennis */}
                {courtType === 'tennis' && selectedCourt && (
                  <div className="mt-6 animate-fade-in">
                    <h4 className="text-md font-medium mb-3">
                      Anzahl der Spieler
                    </h4>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      {[2, 3, 4].map((count) => (
                        <button
                          key={count}
                          type="button"
                          onClick={() => {
                            setTennisPlayerCount(count);
                            // Preserve existing selections where possible
                            const newMemberships = [...playerMemberships];
                            while (newMemberships.length < count) {
                              newMemberships.push('guest');
                            }
                            setPlayerMemberships(newMemberships);
                          }}
                          className={`p-3 sm:p-4 rounded-lg border-2 transition-all font-bold text-lg sm:text-xl min-h-[48px] ${
                            tennisPlayerCount === count
                              ? 'border-red-600 bg-red-600 text-white'
                              : 'border-gray-200 hover:border-red-300 text-gray-700'
                          }`}
                        >
                          {count}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Player Count Selection for Padel Tennis */}
                {courtType === 'padel' && selectedCourt && (
                  <div className="mt-6 animate-fade-in">
                    <h4 className="text-md font-medium mb-3">
                      Anzahl der Spieler
                    </h4>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      {[2, 3, 4].map((count) => (
                        <button
                          key={count}
                          type="button"
                          onClick={() => {
                            setPadelPlayerCount(count);
                            // Preserve existing selections where possible
                            const newMemberships = [...playerMemberships];
                            while (newMemberships.length < count) {
                              newMemberships.push('guest');
                            }
                            setPlayerMemberships(newMemberships);
                          }}
                          className={`p-3 sm:p-4 rounded-lg border-2 transition-all font-bold text-lg sm:text-xl min-h-[48px] ${
                            padelPlayerCount === count
                              ? 'border-red-600 bg-red-600 text-white'
                              : 'border-gray-200 hover:border-red-300 text-gray-700'
                          }`}
                        >
                          {count}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Per-Player Membership Selection */}
              <div
                ref={membershipRef}
                className={`svh-card svh-card-lg p-6 sm:p-8 transition-all ${
                  validationErrors.entryMode
                    ? 'bg-red-50 border-2 border-red-300'
                    : ''
                }`}
              >
                <div className="svh-stage">
                  <span className="svh-stage__n">02</span>
                  <h2 className="svh-stage__t">Spieler</h2>
                  <span className="svh-stage__req">erforderlich</span>
                </div>
                <h3 className="svh-label mb-4">
                  Mitgliedschaft pro Spieler
                </h3>

                <div className="space-y-3">
                  {Array.from({ length: courtType === 'tennis' ? tennisPlayerCount : padelPlayerCount }).map((_, index) => (
                    <div
                      key={index}
                      className="group relative"
                      style={{
                        animation: `slideInFromBottom 400ms ease-out ${index * 50}ms both`
                      }}
                    >
                      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-sm font-semibold text-gray-900">
                            Spieler {index + 1}
                          </label>
                          {playerMembershipData[index] && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 rounded-full">
                              <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                              <span className="text-xs font-medium text-green-700">Verifiziert</span>
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const newMemberships = [...playerMemberships];
                              newMemberships[index] = 'guest';
                              setPlayerMemberships(newMemberships);

                              const newExpanded = [...expandedPlayers];
                              newExpanded[index] = false;
                              setExpandedPlayers(newExpanded);

                              const newData = [...playerMembershipData];
                              newData[index] = null;
                              setPlayerMembershipData(newData);

                              const newNumbers = [...playerMembershipNumbers];
                              newNumbers[index] = '';
                              setPlayerMembershipNumbers(newNumbers);

                              const newLastNames = [...playerLastNames];
                              newLastNames[index] = '';
                              setPlayerLastNames(newLastNames);

                              setEntryMode('guest');
                            }}
                            className={`relative overflow-hidden p-3 rounded-lg border-2 transition-all duration-300 text-center ${
                              playerMemberships[index] === 'guest'
                                ? 'border-red-600 bg-red-50 text-red-900 shadow-sm'
                                : 'border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50'
                            }`}
                          >
                            <div className="font-semibold text-sm">Gast</div>
                            <div className="text-xs text-gray-600 mt-0.5">6-8€/h</div>
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              const newMemberships = [...playerMemberships];
                              newMemberships[index] = 'student';
                              setPlayerMemberships(newMemberships);

                              const newExpanded = [...expandedPlayers];
                              newExpanded[index] = false;
                              setExpandedPlayers(newExpanded);

                              const newData = [...playerMembershipData];
                              newData[index] = null;
                              setPlayerMembershipData(newData);

                              const newNumbers = [...playerMembershipNumbers];
                              newNumbers[index] = '';
                              setPlayerMembershipNumbers(newNumbers);

                              const newLastNames = [...playerLastNames];
                              newLastNames[index] = '';
                              setPlayerLastNames(newLastNames);

                              setEntryMode('guest');
                            }}
                            className={`relative overflow-hidden p-3 rounded-lg border-2 transition-all duration-300 text-center ${
                              playerMemberships[index] === 'student'
                                ? 'border-red-600 bg-red-50 text-red-900 shadow-sm'
                                : 'border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50'
                            }`}
                          >
                            <div className="font-semibold text-sm">Student</div>
                            <div className="text-xs text-gray-600 mt-0.5">5-6€/h</div>
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              const newMemberships = [...playerMemberships];
                              newMemberships[index] = 'member';
                              setPlayerMemberships(newMemberships);

                              const newExpanded = [...expandedPlayers];
                              newExpanded[index] = true;
                              setExpandedPlayers(newExpanded);

                              setEntryMode('guest');
                            }}
                            className={`relative overflow-hidden p-3 rounded-lg border-2 transition-all duration-300 text-center ${
                              playerMemberships[index] === 'member'
                                ? 'border-red-600 bg-red-50 text-red-900 shadow-sm'
                                : 'border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50'
                            }`}
                          >
                            <div className="font-semibold text-sm">Mitglied</div>
                            <div className="text-xs text-gray-600 mt-0.5">SV Heinersreuth</div>
                          </button>
                        </div>

                        {playerMemberships[index] === 'member' && expandedPlayers[index] && (
                          <div
                            className="mt-3 overflow-hidden"
                            style={{
                              animation: 'expandDown 400ms cubic-bezier(0.4, 0, 0.2, 1) forwards'
                            }}
                          >
                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                              <div className="space-y-2">
                                <div className="relative">
                                  <input
                                    type="text"
                                    value={playerMembershipNumbers[index] || ''}
                                    onChange={(e) => {
                                      const newNumbers = [...playerMembershipNumbers];
                                      newNumbers[index] = e.target.value;
                                      setPlayerMembershipNumbers(newNumbers);

                                      const newErrors = [...playerMembershipErrors];
                                      newErrors[index] = '';
                                      setPlayerMembershipErrors(newErrors);
                                    }}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all text-sm bg-white"
                                    placeholder="Mitgliedsnummer"
                                  />
                                </div>

                                <div className="relative">
                                  <input
                                    type="text"
                                    value={playerLastNames[index] || ''}
                                    onChange={(e) => {
                                      const newLastNames = [...playerLastNames];
                                      newLastNames[index] = e.target.value;
                                      setPlayerLastNames(newLastNames);

                                      const newErrors = [...playerMembershipErrors];
                                      newErrors[index] = '';
                                      setPlayerMembershipErrors(newErrors);
                                    }}
                                    onKeyDown={async (e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const number = playerMembershipNumbers[index];
                                        const lastName = playerLastNames[index];

                                        if (!number || !lastName) {
                                          const newErrors = [...playerMembershipErrors];
                                          newErrors[index] = 'Bitte Mitgliedsnummer und Nachname eingeben';
                                          setPlayerMembershipErrors(newErrors);
                                          return;
                                        }

                                        // Check if this number is already used by another player
                                        const isDuplicate = playerMembershipNumbers.some((num, idx) =>
                                          idx !== index && num.trim() === number.trim() && playerMembershipData[idx] !== null
                                        );

                                        if (isDuplicate) {
                                          const newErrors = [...playerMembershipErrors];
                                          newErrors[index] = 'Diese Mitgliedsnummer wurde bereits verwendet';
                                          setPlayerMembershipErrors(newErrors);

                                          const newData = [...playerMembershipData];
                                          newData[index] = null;
                                          setPlayerMembershipData(newData);
                                          return;
                                        }

                                        try {
                                          const result = await verifyBookingMembership(number.trim(), lastName.trim());

                                          if (!result.valid) {
                                            const newErrors = [...playerMembershipErrors];
                                            newErrors[index] = result.error || 'Mitgliedsnummer und Nachname stimmen nicht überein';
                                            setPlayerMembershipErrors(newErrors);

                                            const newData = [...playerMembershipData];
                                            newData[index] = null;
                                            setPlayerMembershipData(newData);
                                            return;
                                          }

                                          const newData = [...playerMembershipData];
                                          newData[index] = {
                                            first_name: result.firstName!,
                                            last_name: result.lastName!,
                                            membership_type: result.membershipType!
                                          };
                                          setPlayerMembershipData(newData);

                                          const newErrors = [...playerMembershipErrors];
                                          newErrors[index] = '';
                                          setPlayerMembershipErrors(newErrors);
                                        } catch {
                                          const newErrors = [...playerMembershipErrors];
                                          newErrors[index] = 'Fehler bei der Überprüfung';
                                          setPlayerMembershipErrors(newErrors);
                                        }
                                      }
                                    }}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all text-sm bg-white"
                                    placeholder="Nachname"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const number = playerMembershipNumbers[index];
                                    const lastName = playerLastNames[index];

                                    if (!number || !lastName) {
                                      const newErrors = [...playerMembershipErrors];
                                      newErrors[index] = 'Bitte Mitgliedsnummer und Nachname eingeben';
                                      setPlayerMembershipErrors(newErrors);
                                      return;
                                    }

                                    // Check if this number is already used by another player
                                    const isDuplicate = playerMembershipNumbers.some((num, idx) =>
                                      idx !== index && num.trim() === number.trim() && playerMembershipData[idx] !== null
                                    );

                                    if (isDuplicate) {
                                      const newErrors = [...playerMembershipErrors];
                                      newErrors[index] = 'Diese Mitgliedsnummer wurde bereits verwendet';
                                      setPlayerMembershipErrors(newErrors);

                                      const newData = [...playerMembershipData];
                                      newData[index] = null;
                                      setPlayerMembershipData(newData);
                                      return;
                                    }

                                    try {
                                      const result = await verifyBookingMembership(number.trim(), lastName.trim());

                                      if (!result.valid) {
                                        const newErrors = [...playerMembershipErrors];
                                        newErrors[index] = result.error || 'Mitgliedsnummer und Nachname stimmen nicht überein';
                                        setPlayerMembershipErrors(newErrors);

                                        const newData = [...playerMembershipData];
                                        newData[index] = null;
                                        setPlayerMembershipData(newData);
                                        return;
                                      }

                                      const newData = [...playerMembershipData];
                                      newData[index] = {
                                        first_name: result.firstName!,
                                        last_name: result.lastName!,
                                        membership_type: result.membershipType!
                                      };
                                      setPlayerMembershipData(newData);

                                      const newErrors = [...playerMembershipErrors];
                                      newErrors[index] = '';
                                      setPlayerMembershipErrors(newErrors);
                                    } catch {
                                      const newErrors = [...playerMembershipErrors];
                                      newErrors[index] = 'Fehler bei der Überprüfung';
                                      setPlayerMembershipErrors(newErrors);
                                    }
                                  }}
                                  className="w-full mt-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-all duration-200 text-sm hover:shadow-md active:scale-95"
                                >
                                  Prüfen
                                </button>
                              </div>

                              {playerMembershipErrors[index] && (
                                <div
                                  className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md"
                                  style={{
                                    animation: 'shake 400ms ease-in-out'
                                  }}
                                >
                                  <p className="text-xs text-red-800 font-medium">{playerMembershipErrors[index]}</p>
                                </div>
                              )}

                              {playerMembershipData[index] && !playerMembershipErrors[index] && (
                                <div
                                  className="mt-2 p-2 bg-green-50 border border-green-300 rounded-md"
                                  style={{
                                    animation: 'slideInFromBottom 300ms ease-out'
                                  }}
                                >
                                  <div className="flex items-center gap-2">
                                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                                      <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" viewBox="0 0 24 24" stroke="currentColor">
                                        <path d="M5 13l4 4L19 7"></path>
                                      </svg>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-semibold text-green-900 truncate">
                                        {playerMembershipData[index]!.first_name} {playerMembershipData[index]!.last_name}
                                      </p>
                                      <p className="text-xs text-green-700">
                                        {playerMembershipData[index]!.membership_type === 'padel' && 'Padel Mitglied'}
                                        {playerMembershipData[index]!.membership_type === 'tennis' && 'Tennis Mitglied'}
                                        {playerMembershipData[index]!.membership_type === 'combination' && 'Kombimitglied'}
                                        {playerMembershipData[index]!.membership_type === 'other' && 'Mitglied'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Date and Time Selection */}
              <div
                ref={dateTimeRef}
                className={`svh-card svh-card-lg p-6 sm:p-8 transition-all ${
                  validationErrors.dateTime
                    ? 'bg-red-50 border-2 border-red-300'
                    : ''
                }`}
              >
                <div className="svh-stage">
                  <span className="svh-stage__n">03</span>
                  <h2 className="svh-stage__t">Datum &amp; Zeit</h2>
                  <span className="svh-stage__req">erforderlich</span>
                </div>
                <h3 className="svh-label mb-4">
                  Datum und Uhrzeit wählen
                </h3>

                {selectedCourt ? (
                  <div className="overflow-hidden rounded-[16px] border border-[color:var(--svh-border)] shadow-[var(--shadow-sm)]">
                    {/* ── Premium date header ── */}
                    <div style={{ background: '#14171B', padding: '20px 22px 18px', position: 'relative' }}>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px' }}>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '6px' }}>Buchungsdatum</div>
                          <div style={{ fontSize: '22px', fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                            {date ? formatDateDisplay(date) : <span style={{ opacity: 0.6 }}>Datum wählen</span>}
                          </div>
                          {date && (
                            <div style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.68)', marginTop: '5px', letterSpacing: '0.01em' }}>{getDaysUntilBooking()}</div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '6px' }}>Platz</div>
                          <div style={{ fontSize: '17px', fontWeight: 800, color: '#fff', letterSpacing: '-0.01em', lineHeight: 1.1 }}>{selectedCourt}</div>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.68)', marginTop: '5px' }}>{courtType === 'tennis' ? 'Tennis' : 'Padel-Tennis'}</div>
                        </div>
                      </div>
                    </div>

                    {/* ── Calendar ── */}
                    <div style={{ background: '#FFFFFF', padding: '22px 8px 18px', borderTop: '1px solid rgba(32,24,20,0.09)' }}>
                      <div className="mobileCalendarFix">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={handleDateSelect}
                          defaultMonth={date}
                          disabled={getDisabledDays()}
                          showOutsideDays={false}
                          className="bg-transparent mx-auto"
                          formatters={{
                            formatWeekdayName: (date) => {
                              const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
                              return days[date.getDay()];
                            },
                          }}
                        />
                      </div>
                    </div>

                    {/* ── Divider with label ── */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '0 16px', margin: '2px 0' }}>
                      <div style={{ flex: 1, height: '1px', background: '#f0f0f0' }} />
                      <span style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#ccc', whiteSpace: 'nowrap' }}>Uhrzeit wählen</span>
                      <div style={{ flex: 1, height: '1px', background: '#f0f0f0' }} />
                    </div>

                    {/* ── Time picker ── */}
                    <div style={{ background: '#fafafa', padding: '12px 14px 16px' }}>
                      <TimeRangePicker
                        openFrom="08:00"
                        openTo="23:00"
                        intervalMinutes={30}
                        minDurationMinutes={30}
                        busyIntervals={busyIntervals}
                        quickPicks={[60, 90, 120]}
                        initial={{
                          start: selectedTime || null,
                          end: selectedEndTime || null,
                        }}
                        onChange={({ start, end }) => {
                          setSelectedTime(start ?? '');
                          setSelectedEndTime(end ?? '');

                          if (date) {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            setSelectedDate(`${year}-${month}-${day}`);
                          }
                        }}
                      />
                    </div>

                    {/* ── Legend ── */}
                    <div style={{ borderTop: '1px solid #f0f0f0', background: '#fff', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#fff', border: '1.5px solid #d1d5db' }} />
                          <span style={{ fontSize: '10px', color: '#9ca3af', fontWeight: 500 }}>Frei</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#fee2e2', border: '1.5px solid #fca5a5' }} />
                          <span style={{ fontSize: '10px', color: '#9ca3af', fontWeight: 500 }}>Belegt</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#16a34a' }} />
                          <span style={{ fontSize: '10px', color: '#9ca3af', fontWeight: 500 }}>Gewählt</span>
                        </div>
                      </div>
                      <span style={{ fontSize: '9px', color: '#d1d5db', fontWeight: 600, letterSpacing: '0.04em' }}>Max. 7 Tage</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <p className="text-gray-500">
                      Bitte wählen Sie zuerst einen Platz aus.
                    </p>
                  </div>
                )}
              </div>

              {/* Additional Services - Only for Padel Tennis */}
              {courtType === 'padel' && (
                <div className="mb-8">
                 <div className="svh-stage">
                  <span className="svh-stage__n">Extra</span>
                  <h2 className="svh-stage__t">Extras</h2>
                  <span className="svh-stage__req">optional</span>
                </div>
                <h3 className="svh-label mb-4">
                  Zusatzoptionen für Padel Buchungen
                </h3>

                  <div className="space-y-3">
                    <label className="svh-choice flex items-center gap-3 p-4 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={floodlight}
                        onChange={(e) =>
                          setFloodlight(e.target.checked)
                        }
                        className="w-5 h-5 shrink-0 accent-[#E82828] rounded"
                      />
                      <span className="ml-3 flex-1">
                        Flutlicht (+3€/Stunde)
                      </span>
                    </label>

                    <div className="p-3 bg-gray-50 rounded-lg">
                      <label className="flex items-center hover:bg-gray-100 transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          checked={racketRental}
                          onChange={(e) => {
                            setRacketRental(e.target.checked);
                            if (!e.target.checked)
                              setRacketRentalQuantity(1);
                          }}
                          className="w-5 h-5 shrink-0 accent-[#E82828] rounded"
                        />
                        <span className="ml-3 flex-1">
                          Schlägerverleih (+3€ pro Schläger)
                        </span>
                      </label>

                      {racketRental && (
                        <div className="mt-4 ml-8 rounded-[10px] border border-[color:var(--svh-border)] bg-[color:var(--svh-surface-sub)] p-4">
                          <label className="block text-[13px] font-semibold text-[color:var(--svh-ink)] mb-3">
                            Anzahl der Schläger:
                          </label>
                          <div className="grid grid-cols-4 gap-2">
                            {[1, 2, 3, 4].map((count) => (
                              <button
                                key={count}
                                type="button"
                                onClick={() =>
                                  setRacketRentalQuantity(count)
                                }
                                aria-label={`${count} Schläger`}
                                aria-pressed={racketRentalQuantity === count}
                                className={`svh-focus flex items-center justify-center h-12 rounded-[10px] border-2 transition-all font-bold text-[18px] tabular-nums ${
                                  racketRentalQuantity === count
                                    ? 'border-[color:var(--svh-crest)] bg-[color:var(--svh-crest)] text-white shadow-[0_4px_14px_rgba(232,40,40,0.22)]'
                                    : 'border-[color:var(--svh-border-mid)] hover:border-[color:var(--svh-ink-3)] text-[color:var(--svh-ink)] bg-white'
                                }`}
                              >
                                {count}
                              </button>
                            ))}
                          </div>
                          <div className="flex items-baseline justify-between gap-3 mt-4 pt-3 border-t border-[color:var(--svh-border)]">
                            <span className="text-[13px] text-[color:var(--svh-ink-2)]">
                              {racketRentalQuantity} × 3,00 €
                            </span>
                            <p className="text-[17px] font-bold text-[color:var(--svh-ink)] tabular-nums">
                              Gesamt: {racketRentalQuantity * 3}€
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <label className="svh-choice flex items-center gap-3 p-4 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={ballRental}
                        onChange={(e) =>
                          setBallRental(e.target.checked)
                        }
                        className="w-5 h-5 shrink-0 accent-[#E82828] rounded"
                      />
                      <span className="ml-3 flex-1">
                        Bälle (+3€)
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* Personal Information */}
              <div
                ref={personalDataRef}
                className={`svh-card svh-card-lg p-6 sm:p-8 transition-all ${
                  validationErrors.personalData
                    ? 'bg-red-50 border-2 border-red-300'
                    : ''
                }`}
              >
                <div className="svh-stage">
                  <span className="svh-stage__n">04</span>
                  <h2 className="svh-stage__t">Persönliche Daten</h2>
                  <span className="svh-stage__req">erforderlich</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="name"
                    placeholder="Vollständiger Name"
                    value={personalFullName}
                    onChange={(e) => setPersonalFullName(e.target.value)}
                    className="svh-field"
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="E-Mail-Adresse"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="svh-field"
                    required
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Telefonnummer"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="svh-field"
                    required
                  />
                  <label
                    className={`col-span-1 sm:col-span-2 flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 select-none group ${
                      ageConfirmed
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <span className="relative flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={ageConfirmed}
                        onChange={(e) => setAgeConfirmed(e.target.checked)}
                        className="sr-only"
                        required
                      />
                      <span
                        className={`flex items-center justify-center w-6 h-6 rounded-md border-2 transition-all duration-200 ${
                          ageConfirmed
                            ? 'bg-red-600 border-red-600'
                            : 'bg-white border-gray-300 group-hover:border-red-400'
                        }`}
                      >
                        {ageConfirmed && (
                          <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                    </span>
                    <span className="flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-gray-900 leading-tight">
                        Ich bestätige, dass ich mindestens 18 Jahre alt bin
                      </span>
                      <span className="text-xs text-gray-400 font-medium tracking-wide">
                        Age confirmation required · 18+
                      </span>
                    </span>
                  </label>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <GutscheinInput
                    onApply={handleApplyGutschein}
                    onRemove={handleRemoveGutschein}
                    appliedCodes={appliedGutscheine}
                    currentTotal={calculateSubtotal()}
                  />
                </div>
              </div>

              {/* Terms and Conditions */}
              <div
                ref={termsRef}
                className={`svh-card svh-card-lg p-6 sm:p-8 transition-all duration-300 ease-in-out ${
                  validationErrors.terms
                    ? 'bg-red-50 border-2 border-red-300 animate-pulse'
                    : ''
                }`}
              >
                <div className="svh-stage">
                  <span className="svh-stage__n">05</span>
                  <h2 className="svh-stage__t">Prüfen &amp; bezahlen</h2>
                  <span className="svh-stage__req">erforderlich</span>
                </div>
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) =>
                      setAcceptTerms(e.target.checked)
                    }
                    className="w-5 h-5 shrink-0 accent-[#E82828] rounded mt-1 cursor-pointer"
                    required
                  />
                  <span className="text-sm text-gray-900 leading-relaxed">
                    Ich akzeptiere die{' '}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        if (onNavigate) {
                          window.history.pushState({}, '', '/buchungsbedingungen');
                          onNavigate('buchungsbedingungen');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                      className="text-red-600 underline hover:text-red-700 transition-all duration-200 font-medium hover:scale-105 inline-block"
                    >
                      Buchungsbedingungen
                    </button>
                    {' '}und akzeptiere die Stornierungsbedingungen: Vollständige Rückerstattung bei Stornierung mehr als 4 Stunden im Voraus, 50% Rückerstattung bei 2-4 Stunden, keine Rückerstattung bei weniger als 2 Stunden vor Buchungsbeginn.
                    {' '}
                    <span className="text-red-600 text-xs font-semibold whitespace-nowrap">
                      (erforderlich)
                    </span>
                  </span>
                </label>
              </div>
            </form>
          </div>

          {/* Sidebar with Pricing and Payment */}
          <div className="lg:col-span-1">
            <div className="svh-card svh-card-lg svh-card-raised overflow-hidden sticky top-24">

              {/* Panel header */}
              <div className="px-6 pt-6 pb-5 border-b border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <span className="svh-sum-label">Buchungsübersicht</span>
                  <span className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="text-[9px] font-bold text-emerald-600 tracking-[0.16em] uppercase">Live</span>
                  </span>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="svh-sum-label mb-1.5">Gesamtbetrag</p>
                    <p className="svh-total">
                      {calculateTotal().toFixed(2)}<span className="text-[20px] font-bold text-[color:var(--svh-crest)] ml-1.5">€</span>
                    </p>
                    {getGutscheinDiscount() > 0 && (
                      <p className="text-[12px] text-emerald-700 font-semibold mt-2">−{getGutscheinDiscount().toFixed(2)} € Gutschein</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPriceModal(true)}
                    className="text-[12px] font-bold text-[color:var(--svh-ink-2)] hover:text-[color:var(--svh-crest)] tracking-[0.12em] uppercase border border-[color:var(--svh-border-mid)] hover:border-[color:var(--svh-crest)] rounded-full px-4 py-2 transition-all duration-200 svh-focus"
                  >
                    Preise
                  </button>
                </div>
              </div>

              <div className="p-5 space-y-3">

                {/* Booking meta: court + time */}
                <div className="rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="divide-y divide-gray-50">
                    <div className="flex items-center justify-between px-4 py-3.5">
                      <span className="svh-sum-label">Platz</span>
                      <span className="svh-sum-value">
                        {courtType === 'tennis' ? 'Tennis' : 'Padel'}{selectedCourt ? ` · ${selectedCourt}` : ''}
                      </span>
                    </div>
                    {date && (
                      <div className="flex items-center justify-between px-4 py-3.5">
                        <span className="svh-sum-label">Datum</span>
                        <span className="svh-sum-value">
                          {date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </span>
                      </div>
                    )}
                    {selectedTime && selectedEndTime && (
                      <div className="flex items-center justify-between px-4 py-3.5">
                        <span className="svh-sum-label">Zeit</span>
                        <span className="svh-sum-value">{selectedTime} – {selectedEndTime} Uhr</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Price breakdown per time segment */}
                {(() => {
                  const breakdown = getPriceBreakdown();
                  const currentPlayerCount = courtType === 'tennis' ? tennisPlayerCount : padelPlayerCount;
                  const membershipLabels: Record<string, string> = { guest: 'Gast', student: 'Student', member: 'Mitglied' };
                  if (breakdown.length === 0) return null;
                  return (
                    <div className="rounded-2xl border border-gray-100 overflow-hidden">
                      <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100 bg-gray-50/60">
                        <span className="svh-sum-label">Platzkalkulation</span>
                        <span className="text-[10px] font-semibold text-gray-400 bg-white border border-gray-200 rounded-full px-2.5 py-0.5">{currentPlayerCount} {currentPlayerCount === 1 ? 'Person' : 'Personen'}</span>
                      </div>
                      <div className="divide-y divide-gray-50">
                        {breakdown.map((seg, si) => (
                          <div key={si} className="px-4 py-3.5">
                            <div className="flex items-center justify-between mb-2.5">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${seg.after4 ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
                                  {seg.after4 ? 'Ab 16 Uhr' : 'Bis 16 Uhr'}
                                </span>
                                <span className="text-[10px] text-gray-400 font-medium">{seg.from} – {seg.to}</span>
                              </div>
                              <span className="text-xs font-bold text-gray-800">{seg.segmentCost.toFixed(2)} €</span>
                            </div>
                            <div className="space-y-1.5 pl-1">
                              {seg.players.map((p) => {
                                const hrs = seg.minutes / 60;
                                const membershipLabel = membershipLabels[playerMemberships[p.idx] || 'guest'] || 'Gast';
                                return (
                                  <div key={p.idx} className="flex items-center justify-between">
                                    <span className="text-[11px] text-gray-500">
                                      Spieler {p.idx + 1}
                                      <span className="ml-1 text-gray-400">({membershipLabel})</span>
                                    </span>
                                    <span className="text-[11px] text-gray-600 font-medium">
                                      {p.ratePerHour === 0
                                        ? <span className="text-emerald-600 font-bold">Frei</span>
                                        : <>{p.ratePerHour} €/h × {hrs % 1 === 0 ? hrs : hrs.toFixed(2)} h = <span className="font-bold text-gray-900">{p.cost.toFixed(2)} €</span></>
                                      }
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Extras */}
                {(floodlight || racketRental || ballRental) && (
                  <div className="rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/60">
                      <span className="svh-sum-label">Zusatzleistungen</span>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {floodlight && (
                        <div className="flex items-center justify-between px-4 py-3.5">
                          <div>
                            <span className="svh-sum-row font-semibold">Flutlicht</span>
                            <p className="text-[12px] text-[color:var(--svh-ink-3)] mt-1">3,00 €/h × {calculateDurationHours()} h</p>
                          </div>
                          <span className="svh-sum-value">{(3 * calculateDurationHours()).toFixed(2)} €</span>
                        </div>
                      )}
                      {racketRental && (
                        <div className="flex items-center justify-between px-4 py-3.5">
                          <div>
                            <span className="svh-sum-row font-semibold">Schlägerverleih</span>
                            <p className="text-[12px] text-[color:var(--svh-ink-3)] mt-1">3,00 € × {racketRentalQuantity}</p>
                          </div>
                          <span className="svh-sum-value">{getRacketRentalPrice().toFixed(2)} €</span>
                        </div>
                      )}
                      {ballRental && (
                        <div className="flex items-center justify-between px-4 py-3.5">
                          <div>
                            <span className="svh-sum-row font-semibold">Leihbälle</span>
                            <p className="text-[12px] text-[color:var(--svh-ink-3)] mt-1">3 Bälle pro Set</p>
                          </div>
                          <span className="svh-sum-value">3,00 €</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Gutschein discount */}
                {getGutscheinDiscount() > 0 && (
                  <div className="rounded-2xl bg-emerald-50 border border-emerald-100 px-4 py-3.5 flex items-center justify-between">
                    <div>
                      <span className="text-[13px] font-bold text-emerald-800">Gutschein-Rabatt</span>
                      <p className="text-[10px] text-emerald-600 mt-0.5">von {calculateSubtotal().toFixed(2)} €</p>
                    </div>
                    <span className="text-[13px] font-black text-emerald-700">−{getGutscheinDiscount().toFixed(2)} €</span>
                  </div>
                )}

                {/* Total row */}
                <div className="rounded-[16px] px-5 py-5 flex items-end justify-between gap-4" style={{ background: '#14171B' }}>
                  <span className="text-[12px] font-bold uppercase tracking-[0.12em]" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    {getGutscheinDiscount() > 0 ? 'Zu zahlen' : 'Gesamt'}
                  </span>
                  <div className="text-right">
                    <span className="svh-total" style={{ color: '#FFFFFF' }}>
                      {calculateTotal().toFixed(2)} €
                    </span>
                    {getGutscheinDiscount() > 0 && (
                      <p className="text-[13px] font-semibold mt-1.5" style={{ color: '#6FC095' }}>
                        Sie sparen {getGutscheinDiscount().toFixed(2)} €
                      </p>
                    )}
                  </div>
                </div>

              </div>

              {/* Payment CTAs inside sidebar */}
              <div className="px-5 pb-6 pt-1">
                <input type="hidden" name="name" value={personalFullName} />
                <input type="hidden" name="email" value={formData.email} />
                <input type="hidden" name="phone_hidden" value={formData.phone} />
                <input type="hidden" name="players_json" value={JSON.stringify(buildPlayersInfo())} />
                <input type="hidden" name="courtKey" value={getCourtKey()} />
                <input type="hidden" name="start" value={selectedDate && selectedTime ? `${selectedDate}T${selectedTime}` : ''} />
                <input type="hidden" name="duration" value={String(calculateDurationHours() * 60)} />
                <input type="hidden" name="membership" value={membershipType} />
                <input type="hidden" name="extras" value={JSON.stringify({ floodlight, racketRental, ballRental })} />
                <input type="hidden" name="totalEUR" value={String(calculateTotal())} />
                <input type="hidden" name="player_number" value={String(courtType === 'tennis' ? tennisPlayerCount : padelPlayerCount)} />

                {calculateTotal() === 0 ? (
                  <>
                    <button
                      type="button"
                      className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white py-4 px-6 rounded-2xl font-bold text-[15px] tracking-wide transition-all duration-200 shadow-[0_4px_20px_rgba(22,163,74,0.35)] hover:shadow-[0_6px_28px_rgba(22,163,74,0.45)] disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.01] active:scale-[0.99]"
                      onClick={handleFreeBooking}
                      disabled={isFreeBookingProcessing}
                    >
                      {isFreeBookingProcessing ? (
                        <div className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Wird gebucht...</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                          <span>Jetzt buchen</span>
                        </div>
                      )}
                    </button>
                    {freeBookingError && <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl"><p className="text-sm text-red-700">{freeBookingError}</p></div>}
                    <p className="text-[10px] text-gray-400 text-center mt-3 tracking-[0.12em] uppercase">Kostenlose Buchung · Sofortige Bestätigung</p>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="w-full bg-red-600 hover:bg-red-700 text-white py-4 px-6 rounded-2xl font-bold text-[15px] tracking-wide transition-all duration-200 shadow-[0_4px_20px_rgba(220,38,38,0.35)] hover:shadow-[0_6px_28px_rgba(220,38,38,0.45)] hover:scale-[1.01] active:scale-[0.99]"
                      onClick={() => { if (validateAndScrollToIncomplete()) setShowPaymentModal(true); }}
                    >
                      <div className="flex items-center justify-center gap-2.5">
                        <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                        <span>Zur Zahlung</span>
                      </div>
                    </button>
                    <div className="flex items-center justify-center gap-2 mt-3">
                      <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                      <p className="text-[10px] text-gray-400 tracking-[0.12em] uppercase">Sichere Zahlung · Sofortige Bestätigung</p>
                    </div>
                  </>
                )}
              </div>

            </div>
          </div>

        </div>
        </div>{/* end bookingFormRef wrapper */}
      </div>

      {/* Price modal portal — rendered at body level */}
      {showPriceModal && ReactDOM.createPortal(
                <div
                  style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px',
                    backgroundColor: 'rgba(10,10,15,0.7)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                  }}
                  onClick={() => setShowPriceModal(false)}
                >
                  <div
                    style={{
                      background: '#ffffff',
                      borderRadius: '20px',
                      boxShadow: '0 32px 80px rgba(0,0,0,0.28), 0 0 0 1px rgba(0,0,0,0.06)',
                      width: '100%',
                      maxWidth: '640px',
                      maxHeight: '90vh',
                      overflowY: 'auto',
                      position: 'relative',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Modal Header */}
                    <div style={{ padding: '32px 32px 0' }}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="inline-block w-5 h-0.5 bg-red-600 rounded-full"></span>
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-red-600">Padel · SV Heinersreuth</p>
                          </div>
                          <h2 className="svh-display-lg" style={{ fontSize: '34px' }}>Preisübersicht</h2>
                          <p className="text-sm text-gray-400 mt-1.5 font-medium">Neue Preise ab 01.01.2026 · Pro Person / Stunde</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowPriceModal(false)}
                          className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors flex-shrink-0 ml-6 mt-1"
                        >
                          <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Modal Body */}
                    <div style={{ padding: '24px 32px 32px' }}>

                      {/* Main price table */}
                      <div className="rounded-2xl overflow-hidden border border-gray-100 mb-4">
                        <div className="grid grid-cols-3 bg-gray-900 text-white">
                          <div className="px-4 py-3">
                            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Kategorie</span>
                          </div>
                          <div className="px-4 py-3 text-center border-l border-gray-700">
                            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">8–16 Uhr</span>
                          </div>
                          <div className="px-4 py-3 text-center border-l border-gray-700">
                            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">16–23 Uhr</span>
                          </div>
                        </div>

                        {[
                          { label: 'Padel Mitglieder & Kombimitgliedschaft', day: '0,00 €', evening: '0,00 €', free: true },
                          { label: 'Studenten (mit Nachweis)', day: '5,00 €', evening: '6,00 €', free: false },
                          { label: 'Mitglieder SVH (TL, Tennis, Fußball, Gymnastik, Kegeln)', day: '4,00 €', evening: '5,00 €', free: false },
                          { label: 'Gäste / Nichtmitglieder', day: '6,00 €', evening: '8,00 €', free: false },
                        ].map((row, i) => (
                          <div
                            key={i}
                            className={`grid grid-cols-3 border-t border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
                          >
                            <div className="px-4 py-4 flex items-center">
                              <span className="text-sm text-gray-700 leading-snug">{row.label}</span>
                            </div>
                            <div className={`px-4 py-4 flex items-center justify-center border-l border-gray-100`}>
                              <span className={`text-sm font-bold ${row.free ? 'text-emerald-600' : 'text-gray-900'}`}>{row.day}</span>
                            </div>
                            <div className="px-4 py-4 flex items-center justify-center border-l border-gray-100">
                              <span className={`text-sm font-bold ${row.free ? 'text-emerald-600' : 'text-gray-900'}`}>{row.evening}</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Extras table */}
                      <div className="rounded-2xl overflow-hidden border border-gray-100">
                        <div className="grid grid-cols-3 bg-gray-50 border-b border-gray-100">
                          <div className="px-4 py-2.5">
                            <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Zusatzleistungen</span>
                          </div>
                          <div className="px-4 py-2.5 text-center border-l border-gray-100">
                            <span className="text-xs font-bold uppercase tracking-widest text-gray-400">8–16 Uhr</span>
                          </div>
                          <div className="px-4 py-2.5 text-center border-l border-gray-100">
                            <span className="text-xs font-bold uppercase tracking-widest text-gray-400">16–23 Uhr</span>
                          </div>
                        </div>
                        {[
                          { label: 'Flutlicht (1 Std. / Platz)', day: '–', evening: '3,00 €' },
                          { label: 'Leihschläger', day: '3,00 €', evening: '3,00 €' },
                          { label: 'Leihbälle (3 Stück)', day: '3,00 €', evening: '3,00 €' },
                          { label: 'Bei Schlägerverlust', day: '80,00 €', evening: '80,00 €' },
                        ].map((row, i) => (
                          <div
                            key={i}
                            className={`grid grid-cols-3 border-t border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
                          >
                            <div className="px-4 py-3.5 flex items-center">
                              <span className="text-sm text-gray-700">{row.label}</span>
                            </div>
                            <div className="px-4 py-3.5 flex items-center justify-center border-l border-gray-100">
                              <span className="text-sm font-semibold text-gray-900">{row.day}</span>
                            </div>
                            <div className="px-4 py-3.5 flex items-center justify-center border-l border-gray-100">
                              <span className="text-sm font-semibold text-gray-900">{row.evening}</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      <p className="mt-5 text-xs text-gray-400 text-center leading-relaxed">
                        Alle Preise in Euro inkl. MwSt. · Änderungen vorbehalten.
                      </p>
                    </div>
                  </div>
                </div>,
                document.body
              )}

              {/* Legacy payment form for paypal module — keep hidden inputs */}
              <form id="paypal-form" style={{ display: 'none' }}>
              <input type="hidden" name="name" value={personalFullName} />
              <input
                type="hidden"
                name="email"
                value={formData.email}
              />
              <input
                type="hidden"
                name="phone_hidden"
                value={formData.phone}
              />
              <input
                type="hidden"
                name="players_json"
                value={JSON.stringify(buildPlayersInfo())}
              />
              <input
                type="hidden"
                name="courtKey"
                value={getCourtKey()}
              />
              <input
                type="hidden"
                name="start"
                value={
                  selectedDate && selectedTime
                    ? `${selectedDate}T${selectedTime}`
                    : ''
                }
              />
              <input
                type="hidden"
                name="duration"
                value={String(calculateDurationHours() * 60)}
              />
              <input
                type="hidden"
                name="membership"
                value={membershipType}
              />
              <input
                type="hidden"
                name="extras"
                value={JSON.stringify({
                  floodlight,
                  racketRental,
                  ballRental,
                })}
              />
              <input
                type="hidden"
                name="totalEUR"
                value={String(calculateTotal())}
              />
              <input
                type="hidden"
                name="player_number"
                value={String(courtType === 'tennis' ? tennisPlayerCount : padelPlayerCount)}
              />

              {/* Payment Method Modal */}
              <PaymentMethodModal
                isOpen={showPaymentModal}
                onClose={() => {
                  if (!isPaymentProcessing) {
                    setShowPaymentModal(false);
                    setPaymentError('');
                  }
                }}
                totalAmount={calculateTotal()}
                isProcessing={isPaymentProcessing}
                errorMessage={paymentError}
                // Stripe checkout handled via n8n webhook (create-checkout) -> redirect to Stripe URL
                onStripeClick={async () => {
                  setPaymentError('');
                  setIsPaymentProcessing(true);

                  try {
                    const payload = buildBookingPayload();
                    const courtKey = payload.courtKey;
                    const start = payload.from;
                    const end = payload.to;
                    const email = payload.email;

                    if (!courtKey || !start || !end || !email) {
                      throw new Error('Buchungsdaten unvollständig. Bitte alle Felder ausfüllen.');
                    }

                    const res = await fetch('https://n8n.cogniiq.co/webhook/create-checkout', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        courtKey,
                        start,
                        end,
                        email,
                        name: payload.name,
                        phone: formData.phone,
                        court_id: payload.court_id,
                        duration: payload.duration,
                        membership: payload.membership,
                        timeZone: payload.timeZone,
                        floodlight: payload.floodlight,
                        racketRental,
                        racketRentalQuantity: racketRental ? racketRentalQuantity : 0,
                        ballRental,
                        totalEUR: calculateTotal(),
                        subtotalEUR: calculateSubtotal(),
                        player_number: payload.player_number,
                        players: buildPlayersInfo(),
                      }),
                    });

                    if (!res.ok) {
                      throw new Error(`Checkout konnte nicht gestartet werden (${res.status})`);
                    }

                    const data = await res.json();

                    if (!data.checkoutUrl) {
                      throw new Error('Keine Checkout-URL erhalten. Bitte versuchen Sie es erneut.');
                    }

                    if (appliedGutscheine.length > 0) {
                      sessionStorage.setItem('pendingGutscheine', JSON.stringify({
                        gutscheine: appliedGutscheine,
                        subtotal: calculateSubtotal(),
                        customerEmail: formData.email
                      }));
                    } else {
                      sessionStorage.removeItem('pendingGutscheine');
                    }

                    window.location.href = data.checkoutUrl;
                  } catch (err) {
                    const message = err instanceof Error ? err.message : 'Fehler beim Starten des Checkouts.';
                    setPaymentError(message);
                    setIsPaymentProcessing(false);
                  }
                }}
                onPayPalClick={async (e) => {
                  // Prevent any default behavior or form submission
                  if (e) {
                    e.preventDefault();
                    e.stopPropagation();
                  }

                  // Clear any previous errors and set loading state
                  setPaymentError('');
                  setIsPaymentProcessing(true);

                  const total = calculateTotal();

                  // ⭐ FREE BOOKING PATH (0 EUR)
                  if (total === 0) {
                    try {
                      // Validate all gift codes before touching anything
                      if (appliedGutscheine.length > 0) {
                        const validation = await validateMultipleGutscheine(appliedGutscheine);
                        if (!validation.success) {
                          setPaymentError(validation.error || "Ungültiger Gutscheincode. Bitte überprüfen Sie Ihre Gutscheine.");
                          setIsPaymentProcessing(false);
                          return;
                        }
                      }

                      const payload = {
                        courtKey: getCourtKey(),
                        start:
                          selectedDate && selectedTime
                            ? `${selectedDate}T${selectedTime}`
                            : "",
                        end:
                          selectedDate && selectedEndTime
                            ? `${selectedDate}T${selectedEndTime}`
                            : "",
                        name: personalFullName,
                        email: formData.email,
                        phone: formData.phone,
                        membershipNumber: membershipNumber || null,
                        membershipType,
                        isMember: true,
                        finalPrice: 0,
                        players: buildPlayersInfo(),
                      };

                      const response = await fetch(
                        "https://hook.eu2.make.com/m03gjplor19g6wgripylh29icx3whfj4",
                        {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify(payload),
                        }
                      );

                      if (!response.ok) {
                        throw new Error(`Webhook fehlgeschlagen (${response.status})`);
                      }

                      // Booking confirmed — now safely redeem gift cards
                      if (appliedGutscheine.length > 0) {
                        const subtotal = calculateSubtotal();
                        const result = await applyMultipleGutscheine(
                          appliedGutscheine,
                          subtotal,
                          formData.email
                        );
                        if (!result.success) {
                          console.error("Gutschein redemption error after booking:", result.error);
                        }
                      }

                      const bookingId = `${payload.courtKey}-${payload.start}`;
                      import("@/lib/floodlightClient").then((mod) => {
                        mod.registerFloodlight(
                          bookingId,
                          payload.courtKey,
                          payload.start,
                          payload.end,
                          floodlight
                        ).catch((err) => {
                          console.error("Floodlight registration failed (non-blocking):", err);
                        });
                      }).catch((err) => {
                        console.error("Floodlight module load failed:", err);
                      });

                      // Redirect will happen - modal can stay open
                      window.location.href = "https://svheinersreuth-padel.de/success";
                      return;
                    } catch (err) {
                      console.error("Free booking error:", err);
                      setPaymentError("Fehler bei kostenloser Buchung. Bitte versuchen Sie es erneut.");
                      setIsPaymentProcessing(false);
                      return;
                    }
                  }

                  // ⭐ PAID BOOKING PATH (PayPal)
                  try {
                    console.log('[BookingPage] Starting PayPal payment flow...');

                    // Save gutscheine for after payment
                    if (appliedGutscheine.length > 0) {
                      sessionStorage.setItem('pendingGutscheine', JSON.stringify({
                        gutscheine: appliedGutscheine,
                        subtotal: calculateSubtotal(),
                        customerEmail: formData.email
                      }));
                    } else {
                      sessionStorage.removeItem('pendingGutscheine');
                    }

                    // Import and call PayPal payment module
                    const paypalModule = await import("@/payments/paypal");

                    if (!paypalModule.startPay) {
                      throw new Error('PayPal Modul konnte nicht geladen werden');
                    }

                    console.log('[BookingPage] Calling PayPal startPay...');

                    // This will redirect to PayPal - modal stays open with loading state
                    await paypalModule.startPay();

                    // Note: If we reach here without redirect, something went wrong
                    console.warn('[BookingPage] PayPal startPay returned without redirect');

                  } catch (error) {
                    console.error('[BookingPage] PayPal payment error:', error);
                    const errorMessage = error instanceof Error
                      ? error.message
                      : 'PayPal checkout konnte nicht gestartet werden. Bitte versuchen Sie es erneut.';

                    setPaymentError(errorMessage);
                    setIsPaymentProcessing(false);
                  }
                }}
              />

              </form>

      {/* ── Mobile Sticky Booking Bar ── only on mobile (md:hidden) */}
      {ReactDOM.createPortal(
        <div
          ref={mobileBarRef}
          className="md:hidden"
          style={{
            position: 'fixed',
            top: 'env(safe-area-inset-top, 0px)',
            left: '0px',
            right: '0px',
            zIndex: 9990,
          }}
        >
          {/* Collapsed / always-visible pill bar */}
          <button
            type="button"
            onClick={() => setMobileBarExpanded(prev => !prev)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 20px',
              height: '56px',
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(32px)',
              WebkitBackdropFilter: 'blur(32px)',
              border: 'none',
              borderBottom: mobileBarExpanded ? 'none' : '1px solid rgba(0,0,0,0.06)',
              borderRadius: '0',
              boxShadow: mobileBarExpanded ? 'none' : '0 4px 24px rgba(0,0,0,0.07)',
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            {/* Left: status dot + label */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ position: 'relative', width: '8px', height: '8px', flexShrink: 0 }}>
                <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#22c55e' }} />
              </div>
              <span style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '0.18em', color: 'rgba(0,0,0,0.32)', textTransform: 'uppercase', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                Gesamt
              </span>
            </div>

            {/* Center: total price */}
            <span style={{ fontSize: '22px', fontWeight: 800, color: '#0a0a0a', letterSpacing: '-0.04em', position: 'absolute', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              {calculateTotal().toFixed(2)}<span style={{ fontSize: '13px', fontWeight: 600, color: '#888', marginLeft: '3px' }}>€</span>
            </span>

            {/* Right: chevron pill */}
            <div
              style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                background: mobileBarExpanded ? '#0a0a0a' : '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.25s ease, transform 0.35s cubic-bezier(0.16,1,0.3,1)',
                transform: mobileBarExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                flexShrink: 0,
              }}
            >
              <svg width="10" height="10" fill="none" viewBox="0 0 24 24" stroke={mobileBarExpanded ? '#fff' : 'rgba(0,0,0,0.5)'} strokeWidth={2.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {/* Expanded panel — slides down */}
          <div style={{ overflow: 'hidden', maxHeight: mobileBarExpanded ? '640px' : '0px', transition: 'max-height 0.42s cubic-bezier(0.16, 1, 0.3, 1)' }}>
            <div style={{ background: 'rgba(255,255,255,0.99)', backdropFilter: 'blur(32px)', WebkitBackdropFilter: 'blur(32px)', borderRadius: '0 0 20px 20px', borderTop: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 20px 60px rgba(0,0,0,0.10)', padding: '0 18px 20px', overflowY: 'auto', maxHeight: '600px' }}>

              {/* Booking meta chips */}
              <div style={{ display: 'flex', gap: '6px', padding: '14px 0 12px', borderBottom: '1px solid rgba(0,0,0,0.04)', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '10px', fontWeight: 700, color: '#0a0a0a', background: '#f3f4f6', borderRadius: '8px', padding: '4px 10px', letterSpacing: '0.02em' }}>
                  {courtType === 'tennis' ? 'Tennis' : 'Padel'}{selectedCourt ? ` · ${selectedCourt}` : ''}
                </span>
                {selectedTime && selectedEndTime && (
                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#0a0a0a', background: '#f3f4f6', borderRadius: '8px', padding: '4px 10px', letterSpacing: '0.02em' }}>
                    {selectedTime} – {selectedEndTime}
                  </span>
                )}
                {date && (
                  <span style={{ fontSize: '10px', fontWeight: 600, color: '#666', background: '#fafafa', border: '1px solid #ebebeb', borderRadius: '8px', padding: '4px 10px' }}>
                    {date.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })}
                  </span>
                )}
              </div>

              {/* Price breakdown segments */}
              {(() => {
                const breakdown = getPriceBreakdown();
                const membershipLabels: Record<string, string> = { guest: 'Gast', student: 'Student', member: 'Mitglied' };
                if (breakdown.length === 0) return null;
                return (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '0.18em', color: 'rgba(0,0,0,0.3)', textTransform: 'uppercase' }}>Platzkalkulation</span>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setShowPriceModal(true); }} style={{ fontSize: '12px', fontWeight: 700, color: '#E82828', background: 'none', border: 'none', cursor: 'pointer', padding: 0, letterSpacing: '0.04em', textDecoration: 'underline', textUnderlineOffset: '3px' }}>Preisübersicht</button>
                    </div>
                    {breakdown.map((seg, si) => (
                      <div key={si} style={{ background: seg.after4 ? '#fffbeb' : '#f8faff', borderRadius: '12px', padding: '10px 12px', marginBottom: '6px', border: `1px solid ${seg.after4 ? '#fde68a' : '#e0e8ff'}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <span style={{ fontSize: '9px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: seg.after4 ? '#92400e' : '#3b5bdb' }}>
                            {seg.after4 ? 'Ab 16 Uhr' : 'Bis 16 Uhr'} · {seg.from}–{seg.to}
                          </span>
                          <span style={{ fontSize: '12px', fontWeight: 800, color: '#0a0a0a', letterSpacing: '-0.02em' }}>{seg.segmentCost.toFixed(2)} €</span>
                        </div>
                        {seg.players.map((p) => {
                          const hrs = seg.minutes / 60;
                          const membershipLabel = membershipLabels[playerMemberships[p.idx] || 'guest'] || 'Gast';
                          return (
                            <div key={p.idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '3px', paddingTop: '3px', borderTop: '1px solid rgba(0,0,0,0.03)' }}>
                              <span style={{ fontSize: '10px', color: '#666', letterSpacing: '0.01em' }}>Spieler {p.idx + 1} <span style={{ color: '#aaa', fontSize: '9px' }}>({membershipLabel})</span></span>
                              <span style={{ fontSize: '10px', fontWeight: 700, color: '#333', letterSpacing: '-0.01em' }}>
                                {p.ratePerHour === 0
                                  ? <span style={{ color: '#16a34a', fontWeight: 800 }}>Frei</span>
                                  : <>{p.ratePerHour} €/h × {hrs % 1 === 0 ? hrs : hrs.toFixed(2)} h = <strong style={{ color: '#0a0a0a' }}>{p.cost.toFixed(2)} €</strong></>
                                }
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Extras */}
              {(floodlight || racketRental || ballRental) && (
                <div style={{ marginTop: '10px', borderTop: '1px solid rgba(0,0,0,0.04)', paddingTop: '10px' }}>
                  <span style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '0.18em', color: 'rgba(0,0,0,0.3)', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>Extras</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {floodlight && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#fafafa', borderRadius: '8px', border: '1px solid #ebebeb' }}>
                        <span style={{ fontSize: '11px', color: '#555', letterSpacing: '0.01em' }}>Flutlicht · 3,00 €/h × {calculateDurationHours()} h</span>
                        <span style={{ fontSize: '11px', fontWeight: 800, color: '#0a0a0a' }}>{(3 * calculateDurationHours()).toFixed(2)} €</span>
                      </div>
                    )}
                    {racketRental && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#fafafa', borderRadius: '8px', border: '1px solid #ebebeb' }}>
                        <span style={{ fontSize: '11px', color: '#555' }}>Schläger ({racketRentalQuantity}×) · 3,00 €</span>
                        <span style={{ fontSize: '11px', fontWeight: 800, color: '#0a0a0a' }}>{getRacketRentalPrice().toFixed(2)} €</span>
                      </div>
                    )}
                    {ballRental && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#fafafa', borderRadius: '8px', border: '1px solid #ebebeb' }}>
                        <span style={{ fontSize: '11px', color: '#555' }}>Leihbälle</span>
                        <span style={{ fontSize: '11px', fontWeight: 800, color: '#0a0a0a' }}>3,00 €</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Gutschein */}
              {getGutscheinDiscount() > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', padding: '8px 12px', background: '#f0fdf4', borderRadius: '10px', border: '1px solid #bbf7d0' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#15803d', letterSpacing: '0.01em' }}>Gutschein-Rabatt</span>
                  <span style={{ fontSize: '12px', fontWeight: 800, color: '#15803d', letterSpacing: '-0.02em' }}>−{getGutscheinDiscount().toFixed(2)} €</span>
                </div>
              )}

              {/* Total row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '14px', paddingTop: '14px', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                <div>
                  <span style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '0.18em', color: 'rgba(0,0,0,0.3)', textTransform: 'uppercase', display: 'block', marginBottom: '2px' }}>
                    {getGutscheinDiscount() > 0 ? 'Zu zahlen' : 'Gesamt'}
                  </span>
                  <span style={{ fontSize: '9px', color: '#bbb', letterSpacing: '0.04em' }}>inkl. MwSt.</span>
                </div>
                <span style={{ fontSize: '28px', fontWeight: 900, color: '#0a0a0a', letterSpacing: '-0.05em', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                  {calculateTotal().toFixed(2)}<span style={{ fontSize: '16px', fontWeight: 600, color: '#888', marginLeft: '2px' }}>€</span>
                </span>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default BookingPage;
